import { deviceSession } from '@/privacy/deviceStorage';

/**
 * PX-14 (`docs/concept-design-agency-production-and-automation.md` §24.5, ADR-191): what the
 * invitation page does with the one tap a customer's link is. Signed out, it enters first; the
 * server answers a session, or says by code why this link is not one to enter by, and the page then
 * goes on as it always did (sign in, or create an account).
 */
export type OneTapEntryStateType =
  /** Not decided yet: the session check has not answered. */
  | { kind: 'waiting' }
  /**
   * The mail's link, signed out: one press on "Open your request page" enters (review M3). Never
   * on load, so a mail scanner that runs the page spends nothing and gets no session; and the
   * press is the act the terms line beside it is accepted by (D-0430).
   */
  | { kind: 'ready' }
  | { kind: 'entering' }
  /** Not a link to enter by (a member's, or an address with a password): the preview flow. */
  | { kind: 'fallback' }
  /** It ran out; one tap sends a fresh one. */
  | { kind: 'expired'; sending: boolean; sentTo: string | null; sendFailed: boolean }
  /** Entered once already, on another phone; one tap mails a sign-in link to its own address. */
  | {
      kind: 'used';
      sending: boolean;
      sentTo: string | null;
      sendFailed: boolean;
      /** Re-review N1, N7: links went to the address lately; the server says no more than that. */
      limited?: boolean;
    }
  | { kind: 'entered' }
  /** Principle review 19: offline, or the server failed; nothing was spent, and Try again asks again. */
  | { kind: 'failed' }
  /**
   * A sign-in link (`?signIn=1`) that was spent or ran out. Principle review 20: one tap asks for a
   * new one, from the link itself, to the account's own address.
   */
  | { kind: 'signInGone'; sending?: boolean; sent?: boolean; sendFailed?: boolean }
  /**
   * Principle review 18: a link that was withdrawn, declined or is not known. Nothing can be sent
   * from it; the customer may name their address for a sign-in link instead.
   */
  | { kind: 'noLongerWorks'; reason: LinkGoneReasonType };

function refusalOf(error: unknown): { status: number | null; code: string | null } {
  const response = (error as { response?: { status?: unknown; data?: { code?: unknown } } } | null)
    ?.response;
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    code: typeof response?.data?.code === 'string' ? response.data.code : null,
  };
}

/**
 * Where a refused entry goes. Only an expired or used link is said here; everything else —
 * an older server without the door (404), a sign-in it needs, a refusal the preview words better —
 * goes on to the preview, which says it as it always has.
 */
export function entryRefusalState(error: unknown): OneTapEntryStateType {
  const { status, code } = refusalOf(error);
  if (code === 'invitation_expired') {
    return { kind: 'expired', sending: false, sentTo: null, sendFailed: false };
  }
  if (code === 'invitation_used') {
    return { kind: 'used', sending: false, sentTo: null, sendFailed: false };
  }
  // Principle review 19: no answer (offline) or the server's own failure is said, with Try again.
  if (status === null || status >= 500) return { kind: 'failed' };
  return { kind: 'fallback' };
}

/**
 * Principle review 18: why a link the preview refused no longer works, by the code the server gives
 * it; `null` for a refusal that is not about the link (and an older server, which gives none).
 */
export function linkGoneReason(error: unknown): LinkGoneReasonType | null {
  const { status, code } = refusalOf(error);
  if (code === 'invitation_revoked') return 'revoked';
  if (code === 'invitation_declined') return 'declined';
  if (code === 'invitation_ran_out') return 'ranOut';
  if (code === 'invitation_unknown' || status === 400 || status === 404) return 'unknown';
  // An older server gives a gone link no code; it still no longer works.
  if (status === 410) return 'other';
  return null;
}

export type LinkGoneReasonType = 'revoked' | 'declined' | 'ranOut' | 'unknown' | 'other';

/**
 * D-0430: what the one tap says about the terms. Only where the deployment says the tap accepts
 * them does the page show the line and send the version and the sentence it showed; otherwise
 * nothing is sent and the terms screen follows the tap, as for any new account.
 */
export function firstTapTerms(
  acceptsOnFirstTap: boolean | undefined,
  version: string,
  wording: string,
): { acceptedTermsVersion: string; acceptedTermsWording: string } | null {
  return acceptsOnFirstTap === true
    ? { acceptedTermsVersion: version, acceptedTermsWording: wording }
    : null;
}

/**
 * PX-14 (review C1): a mailed invitation's fragment is `token.entry`. The token names the invitation
 * (the creator holds it too); the entry secret is only in the mail, and only it lets the link enter.
 */
export function invitationFragmentParts(fragment: string): { token: string; entry: string | null } {
  const [token = '', entry = ''] = fragment.replace(/^#/, '').trim().split('.', 2);
  return { token, entry: entry || null };
}

/** Whether the page was opened by a sign-in link rather than an invitation (`?signIn=1`). */
export function isSignInLink(search: string): boolean {
  return new URLSearchParams(search).get('signIn') === '1';
}

/**
 * I-2461 (PX-14b): whether a sign-in link opens the one thing a moment's mail named (`&opens=item`),
 * not the request page, so the card says what it opens. A hint only, no credential.
 */
export function signInOpensItem(search: string): boolean {
  return isSignInLink(search) && new URLSearchParams(search).get('opens') === 'item';
}

/** Re-review N1, N7: whether a refused "send me a sign-in link" met the mail limits. */
export function signInLinkLimited(error: unknown): boolean {
  const { status, code } = refusalOf(error);
  return status === 429 && code === 'sign_in_link_limited';
}

/** The browser's own zone and language, so the customer's messages respect their night (PX-15). */
export function browserClock(): { timeZone?: string; language?: string } {
  let timeZone: string | undefined;
  try {
    // i18n-format: machine: reads the browser's zone name, never shown
    timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    timeZone = undefined;
  }
  const language = typeof navigator !== 'undefined' ? navigator.language?.slice(0, 2) : undefined;
  return { timeZone, language: language === 'de' ? 'de' : language ? 'en' : undefined };
}

/** sessionStorage: a passkey is offered once, after a one-tap entry on this phone. */
export const PASSKEY_OFFER_KEY = 'featit:passkeyOffer';

export function rememberPasskeyOffer(): void {
  try {
    deviceSession.setItem(PASSKEY_OFFER_KEY, '1');
  } catch {
    // A browser that keeps nothing is offered nothing; the account works without a passkey.
  }
}

export function passkeyOfferPending(): boolean {
  try {
    return deviceSession.getItem(PASSKEY_OFFER_KEY) === '1';
  } catch {
    return false;
  }
}

export function forgetPasskeyOffer(): void {
  try {
    deviceSession.removeItem(PASSKEY_OFFER_KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}
